/**
 * @file element-typo.ts
 * @description Правка `typo` — цвета и шрифта элемента по частям — в кабинете
 * (phase19.16, партия SB120, этап 3а «элементы»; site-builder.md SB120, SB8,
 * SB10; проект UI docs/phases/phase19.16-materials/uiux-sb120-stage3.md §2;
 * данные db-design-sb120-stage3.md §2, §6).
 *
 * Чистые функции без React:
 *  - `patchTypoPart` — правка одной части: ключ со значением `undefined`
 *    снимается («↺» = «как у роли»), результат — канон shared
 *    (`compactTypo`: без «Авто», пустых частей и пустого `typo`), поэтому
 *    открыть и сохранить блок без правок — тот же payload побайтно;
 *  - `withTypo` — элемент, шапка или надпись на фото с новым `typo`;
 *  - `stripMissingFonts` — копия секции без своих шрифтов, которых нет у
 *    магазина (дублирование блока: сервер отвечает 422
 *    `element_font_not_found` на каждый добавленный неизвестный ключ, §6).
 */

import {
  compactSectionTypos,
  compactTypo,
  sectionTypoFonts,
  type SectionPayload,
  type SiteFontRegistry,
  type TypoPart,
  type TypoValue,
} from "@chaiberi/shared";

/** Правка части: ключ есть со значением `undefined` — снять. */
export type TypoPartPatch = { [K in keyof TypoPart]?: TypoPart[K] | undefined };

/** Часть `typo` с правкой; результат — канон (нет «Авто» и пустых объектов). */
export function patchTypoPart(typo: TypoValue | undefined, key: string, patch: TypoPartPatch): TypoValue | undefined {
  const part: Record<string, unknown> = { ...(typo?.[key] ?? {}) };
  for (const [field, value] of Object.entries(patch)) {
    if (value === undefined) delete part[field];
    else part[field] = value;
  }
  return compactTypo({ ...(typo ?? {}), [key]: part }) as TypoValue | undefined;
}

/** `typo` владельца (элемент, шапка, надпись на фото) — терпимо к форме. */
export function typoOf(owner: object): TypoValue | undefined {
  const typo = (owner as { typo?: unknown }).typo;
  return typeof typo === "object" && typo !== null ? (typo as TypoValue) : undefined;
}

/**
 * Владелец с новым `typo`; `undefined` — ключ снимается. Набор частей
 * задаёт реестр shared (`TYPO_PART_KEYS`), панель пишет только его ключи.
 */
export function withTypo<T extends object>(owner: T, typo: TypoValue | undefined): T {
  const next = { ...owner } as Record<string, unknown>;
  if (typo) next.typo = typo;
  else delete next.typo;
  return next as T;
}

/** Есть ли в секции свой шрифт, которого нет у магазина (удалён). */
export function hasMissingFonts(payload: SectionPayload, registry: SiteFontRegistry): boolean {
  return sectionTypoFonts(payload).some((font) => !registry.has(font.key));
}

/** Объект по пути `a.b.0.c` (части — ключи и индексы); нет — `null`. */
function objectAt(root: unknown, segments: readonly string[]): Record<string, unknown> | null {
  let cur: unknown = root;
  for (const seg of segments) {
    if (typeof cur !== "object" || cur === null) return null;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return typeof cur === "object" && cur !== null ? (cur as Record<string, unknown>) : null;
}

/**
 * Копия секции без своих шрифтов, которых нет в реестре магазина: у части
 * снимается `font` (на сайте и так шрифт роли), остальное `typo` остаётся.
 * Вход не меняется.
 */
export function stripMissingFonts(payload: SectionPayload, registry: SiteFontRegistry): SectionPayload {
  const copy = structuredClone(payload);
  for (const font of sectionTypoFonts(copy)) {
    if (registry.has(font.key)) continue;
    const segments = font.path.split(".");
    const part = objectAt(copy, segments.slice(0, -1));
    if (part) delete part.font;
  }
  return compactSectionTypos(copy);
}
